import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { gamificationService, LEVEL_THRESHOLDS } from "@/services/gamificationService";
import { useAuth } from "@/contexts/AuthContext";
import { Flame, Trophy, Star, Zap, Award } from "lucide-react";

interface GamificationHubProps {
  compact?: boolean;
}

export function GamificationHub({ compact = false }: GamificationHubProps) {
  const { user } = useAuth();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["game-stats", user?.id],
    queryFn: () => gamificationService.getStats(user!.id),
    enabled: !!user?.id,
  });

  const { data: achievements } = useQuery({
    queryKey: ["achievements"],
    queryFn: () => gamificationService.getAchievements(),
  });

  const { data: userAchievements } = useQuery({
    queryKey: ["user-achievements", user?.id],
    queryFn: () => gamificationService.getUserAchievements(user!.id),
    enabled: !!user?.id,
  });

  const currentXP = parseInt(stats?.xp || "0", 10);
  const currentLevel = parseInt(stats?.level || "1", 10);
  const currentStreak = parseInt(stats?.currentStreak || "0", 10);
  const longestStreak = parseInt(stats?.longestStreak || "0", 10);
  const xpProgress = gamificationService.getXPForNextLevel(currentXP);

  const unlockedAchievementIds = new Set(userAchievements?.map(ua => ua.achievementId) || []);

  if (statsLoading) {
    return (
      <Card className="animate-pulse">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="w-4 h-4" />
            Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-8 bg-muted rounded" />
            <div className="h-8 bg-muted rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">{currentLevel}</span>
                </div>
                <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-0.5">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                </div>
              </div>
              <div>
                <div className="font-semibold">Level {currentLevel}</div>
                <div className="text-xs text-muted-foreground">
                  {currentXP.toLocaleString()} XP
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <Flame className={`w-5 h-5 ${currentStreak > 0 ? "text-orange-500" : "text-muted-foreground"}`} />
                <span className="font-semibold">{currentStreak}</span>
                <span className="text-xs text-muted-foreground">day streak</span>
              </div>

              <TooltipProvider>
                <div className="flex -space-x-1">
                  {achievements?.slice(0, 4).map((achievement) => {
                    const isUnlocked = unlockedAchievementIds.has(achievement.id);
                    return (
                      <Tooltip key={achievement.id}>
                        <TooltipTrigger>
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm border-2 border-background ${
                              isUnlocked
                                ? "bg-gradient-to-br from-amber-400 to-orange-500"
                                : "bg-muted opacity-50"
                            }`}
                          >
                            {achievement.icon || "🏆"}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <div className="text-sm font-medium">{achievement.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {isUnlocked ? "Unlocked!" : achievement.description}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                  {achievements && achievements.length > 4 && (
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs border-2 border-background">
                      +{achievements.length - 4}
                    </div>
                  )}
                </div>
              </TooltipProvider>
            </div>
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-muted-foreground">Next level</span>
              <span className="font-medium">{xpProgress.needed.toLocaleString()} XP to go</span>
            </div>
            <Progress value={xpProgress.progress} className="h-2" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          Your Progress
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-2xl">{currentLevel}</span>
            </div>
            <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-1 shadow">
              <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
            </div>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1">
              <span className="font-semibold text-lg">Level {currentLevel}</span>
              <span className="text-sm text-muted-foreground">
                {currentXP.toLocaleString()} / {(LEVEL_THRESHOLDS[currentLevel] || LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1]).toLocaleString()} XP
              </span>
            </div>
            <Progress value={xpProgress.progress} className="h-3" />
            <div className="text-xs text-muted-foreground mt-1">
              {xpProgress.needed.toLocaleString()} XP to level {currentLevel + 1}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-lg bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Flame className="w-5 h-5 text-orange-500" />
              <span className="font-medium">Current Streak</span>
            </div>
            <div className="text-3xl font-bold text-orange-500">{currentStreak}</div>
            <div className="text-xs text-muted-foreground">
              Best: {longestStreak} days
            </div>
          </div>

          <div className="p-4 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-5 h-5 text-blue-500" />
              <span className="font-medium">Total Sessions</span>
            </div>
            <div className="text-3xl font-bold text-blue-500">
              {parseInt(stats?.totalSessions || "0", 10)}
            </div>
            <div className="text-xs text-muted-foreground">
              Learning sessions completed
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="p-3 rounded-lg bg-muted/50">
            <div className="text-2xl font-bold">{parseInt(stats?.totalCardsReviewed || "0", 10)}</div>
            <div className="text-xs text-muted-foreground">Cards Reviewed</div>
          </div>
          <div className="p-3 rounded-lg bg-muted/50">
            <div className="text-2xl font-bold">{parseInt(stats?.totalQuizzesTaken || "0", 10)}</div>
            <div className="text-xs text-muted-foreground">Quizzes Taken</div>
          </div>
          <div className="p-3 rounded-lg bg-muted/50">
            <div className="text-2xl font-bold">{parseInt(stats?.totalWorkoutsCompleted || "0", 10)}</div>
            <div className="text-xs text-muted-foreground">Workouts Done</div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium flex items-center gap-2">
              <Award className="w-4 h-4" />
              Achievements
            </h4>
            <Badge variant="secondary">
              {userAchievements?.length || 0} / {achievements?.length || 0}
            </Badge>
          </div>
          <div className="grid grid-cols-5 gap-2">
            <TooltipProvider>
              {achievements?.map((achievement) => {
                const isUnlocked = unlockedAchievementIds.has(achievement.id);
                return (
                  <Tooltip key={achievement.id}>
                    <TooltipTrigger>
                      <div
                        className={`aspect-square rounded-lg flex items-center justify-center text-xl transition-all ${
                          isUnlocked
                            ? "bg-gradient-to-br from-amber-400 to-orange-500 shadow-lg scale-100"
                            : "bg-muted opacity-40 grayscale"
                        }`}
                      >
                        {achievement.icon || "🏆"}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div className="font-medium">{achievement.name}</div>
                      <div className="text-xs text-muted-foreground max-w-[200px]">
                        {achievement.description}
                      </div>
                      {isUnlocked && (
                        <Badge className="mt-1" variant="secondary">
                          +{achievement.xpReward} XP
                        </Badge>
                      )}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </TooltipProvider>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
